import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import api from '../services/api';
import '../index.css';

import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification
} from "firebase/auth";
import { auth } from "../firebase/config";

function LoginPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  // ✅ NEW — tracks whether to show the "resend verification" option
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const location = useLocation();
  const successMessage = location.state?.message;

  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setShowResendVerification(false);
    setResendMessage('');
  };

  // ================= LOGIN =================

  const handleLogin = async (e) => {
    if (isLoading) return;
    e.preventDefault();

    if (!formData.email || !formData.password) {
      setError("Please enter email and password.");
      return;
    }

    setIsLoading(true);
    setError('');
    setShowResendVerification(false);
    setResendMessage('');

    try {
      // ================= FIREBASE LOGIN =================
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const firebaseUser = userCredential.user;

      // ================= EMAIL VERIFICATION CHECK =================
      // ✅ NEW — block access entirely until the user has verified their email
      if (!firebaseUser.emailVerified) {
        console.warn('⚠️ Login blocked: email not verified for', firebaseUser.email);

        setShowResendVerification(true);

        setError(
          'Please verify your email before logging in. Check your inbox for the verification link we sent you.'
        );

        // Sign out immediately — do not let an unverified user stay authenticated
        await auth.signOut();
        localStorage.clear();

        setIsLoading(false);
        return;
      }

      const token = await firebaseUser.getIdToken();

      // Store base auth info
      localStorage.setItem('token', token);
      localStorage.setItem('userId', firebaseUser.uid);
      localStorage.setItem('userEmail', firebaseUser.email);

      // ================= FETCH USER PROFILE FROM BACKEND =================
      const response = await api.get(`/users/${firebaseUser.uid}`);

      // Support both { user: {...} } and direct object
      let user = response.data?.user || response.data;

      console.log("User object from backend:", user);

      if (!user || !user.userType) {
        // fallback role
        user = {
          userType: 'student'
        };
      }

      // ================= STORE USER DATA =================
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('userType', user.userType);
      localStorage.setItem('userRole', user.userType);
      localStorage.setItem(
        'userName',
        `${user.firstName || ''} ${user.lastName || ''}`.trim()
      );

      if (user.state) {
        localStorage.setItem('userState', user.state);
      }

      if (user.localGovernment) {
        localStorage.setItem('userLGA', user.localGovernment);
      }

      console.log("Navigating as:", user.userType);

      // ================= ROLE NAVIGATION =================
      switch (user.userType) {
        case 'student':
          navigate('/student');
          break;
        case 'teacher':
          navigate('/teacher');
          break;
        case 'parent':
          navigate('/parent');
          break;
        case 'admin':
          navigate('/admin');
          break;
        default:
          navigate('/home');
      }

    } catch (err) {
      console.error('Login error:', err);

      let errorMessage = 'Invalid email or password.';

      if (err.code === 'ECONNABORTED' || err.userMessage) {
        errorMessage = err.userMessage || 'Server is starting up. Please wait 30 seconds and try again ⏳';
      } else if (err.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email.';
      } else if (err.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email format.';
      } else if (err.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password. Please check and try again.';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // ================= RESEND VERIFICATION EMAIL =================
  // ✅ NEW — lets the unverified user request the link again without
  // needing to sign up a second time
  const handleResendVerification = async () => {
    if (!formData.email || !formData.password) {
      setError('Please re-enter your email and password, then try again.');
      return;
    }

    setIsLoading(true);
    setResendMessage('');

    try {
      // sendEmailVerification requires a signed-in user, so we briefly
      // sign back in with the credentials still in the form
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      await sendEmailVerification(userCredential.user, {
        url: 'https://www.edulinker.app/login',
      });

      await auth.signOut();
      localStorage.clear();

      setResendMessage('Verification email resent! Please check your inbox.');
    } catch (err) {
      console.error('Resend verification error:', err);
      setError('Could not resend verification email. Please try logging in again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ================= FORGOT PASSWORD =================
  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!resetEmail) {
      setError('Please enter your email address.');
      return;
    }

    setIsLoading(true);
    setError('');
    setResetMessage('');

    try {
      await sendPasswordResetEmail(auth, resetEmail);

      setResetMessage('Password reset email sent! Check your inbox.');
      setTimeout(() => {
        setShowForgotPassword(false);
      }, 2000);
      setResetEmail('');
    } catch (err) {
      console.error('Password reset error:', err);

      let errorMessage = 'Failed to send reset email. Please try again.';
      if (err.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email.';
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container min-vh-100 d-flex flex-column align-items-center justify-content-center">
      <div className="login-pattern-overlay"></div>
      <div className="login-float-element login-float-1"></div>
      <div className="login-float-element login-float-2"></div>

      <div className="flex-grow-1 d-flex align-items-center justify-content-center w-100 px-3 login-content">
        <div className="w-100" style={{ maxWidth: '420px' }}>
          <div className="login-card bg-white rounded-3 p-4 p-md-5">

            <div className="text-center mb-4">
              <Link to="/" className="text-decoration-none d-inline-block mb-4">
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <img
                    src="/logo.png"
                    alt="Logo"
                    className="login-logo me-2"
                  />
                  <h1 className="edu-logo-text mb-0" style={{ fontSize: '2.5rem' }}>
                    EduLinker
                  </h1>
                </div>
              </Link>

              <p className="login-subtitle text-muted mb-0">
                {showForgotPassword
                  ? 'Reset your password'
                  : 'Enter your credentials to continue'}
              </p>
            </div>

            {successMessage && (
              <div className="alert alert-success border-0 rounded-3 mb-4">
                {successMessage}
              </div>
            )}

            {error && (
              <div className="alert alert-danger border-0 rounded-3 mb-4">
                {error}
              </div>
            )}

            {/* ✅ NEW — Resend verification email option */}
            {showResendVerification && (
              <div className="alert alert-warning border-0 rounded-3 mb-4">
                <p className="mb-2">Didn't get the email?</p>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-dark rounded-3"
                  onClick={handleResendVerification}
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending...' : 'Resend verification email'}
                </button>
              </div>
            )}

            {resendMessage && (
              <div className="alert alert-success border-0 rounded-3 mb-4">
                {resendMessage}
              </div>
            )}

            {resetMessage && (
              <div className="alert alert-success border-0 rounded-3 mb-4">
                {resetMessage}
              </div>
            )}

            {!showForgotPassword ? (
              <form onSubmit={handleLogin}>
                <div className="mb-4">
                  <label className="form-label fw-medium text-dark mb-2">
                    Email address
                  </label>
                  <input
                    type="email"
                    className="form-control py-3 border rounded-3 login-input"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your email"
                    autoComplete="email"
                  />
                </div>

                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label fw-medium text-dark mb-0">
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="btn btn-link text-decoration-none p-0 login-forgot-link"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <input
                    type="password"
                    className="form-control py-3 border rounded-3 login-input"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your password"
                    autoComplete="current-password"
                  />
                </div>

                <div className="d-grid mt-4">
                  <button
                    type="submit"
                    className="btn btn-dark btn-lg py-3 fw-bold rounded-3 border-0 login-btn"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Signing in...
                      </>
                    ) : (
                      "Sign In"
                    )}
                  </button>
                </div>

                <div className="text-center mt-4 pt-3 border-top">
                  <p className="text-muted mb-0">
                    Don't have an account?{' '}
                    <Link
                      to="/get-started"
                      className="text-primary fw-medium text-decoration-none"
                    >
                      Sign up
                    </Link>
                  </p>
                </div>
              </form>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <div className="mb-4">
                  <label className="form-label fw-medium text-dark mb-2">
                    Email address
                  </label>
                  <input
                    type="email"
                    className="form-control py-3 border rounded-3 login-input"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    placeholder="Enter your email"
                    autoComplete="email"
                  />
                </div>

                <div className="d-grid gap-2 mt-4">
                  <button
                    type="submit"
                    className="btn btn-dark btn-lg py-3 fw-bold rounded-3 border-0 login-btn"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Sending...' : 'Send reset link'}
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-primary btn-lg py-3 rounded-3 login-back-btn"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setError('');
                      setResetMessage('');
                    }}
                  >
                    Back to sign in
                  </button>
                </div>
              </form>
            )}
          </div>

          <div className="text-center mt-4">
            <p className="login-footer-text">
              By signing in, you agree to our{' '}
              <Link to="/terms" className="login-footer-link">
                Terms
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="login-footer-link">
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;